import { inject, Injectable } from '@angular/core';
import { IcomplyUserIsActive } from '../../shared/models/icomply.models';
import { UserContextModel } from '../models/user.models';
import { AuthService } from './auth.service';
import { PermissionService } from './permission.service';
import { UserContextService } from './user-context/user-context.service';
import { OnboardingStateService } from './onboarding-state.service';

/**
 * Centralized service to handle all application initialization logic.
 * This service orchestrates the startup sequence with proper dependency ordering
 * and error handling.
 */
@Injectable({
  providedIn: 'root'
})
export class AppInitializationService {
  private readonly authService = inject(AuthService);
  private readonly permissionService = inject(PermissionService);
  private readonly userContextService = inject(UserContextService);
  private readonly onboardingService = inject(OnboardingStateService);

  /**
   * Initialize the application by loading all required data in the correct order.
   * This method is called by the app initializer in app.config.ts.
   *
   * Flow:
   * 1. Check authentication - redirect to log-out if not authenticated
   * 2. Load permissions - critical for routing guards
   * 3. Load user context - critical for app functionality
   * 4. Trigger iComply auto-registration - non-critical, must run before status check
   * 5. Load iComply status - non-critical, continue if fails
   * 6. Load onboarding status - critical for onboarding guard
   *
   * Non-blocking operations (handled elsewhere):
   * - Informal policies (loaded in UserContextService.loadInformalPolicies)
   * - Amplitude initialization (handled in portal-shell after auth confirmed)
   * - Theme setup (handled in portal-shell from user context)
   *
   * @throws Redirects to service-unavailable if critical services fail
   */
  async initialize(): Promise<void> {
    try {
      // Skip initialization for pages that should work without full app init
      const currentUrl = window.location.href.toLowerCase();
      const skipPaths = ['/v2/log-out', '/v2/service-unavailable'];
      const shouldSkipInit = skipPaths.some(path => currentUrl.includes(path.toLowerCase()));

      if (shouldSkipInit) {
        return;
      }

      // Step 1: Check authentication first - everything else requires auth
      if (!this.authService.isAuthenticated()) {
        this.redirectToLogout();
        return;
      }

      // Step 2: Load permissions (required for route guards)
      await this.loadPermissions();

      // Step 3: Load user context (required for app functionality)
      await this.loadUserContext();

      // Step 3b: Trigger iComply auto-registration before the status check below.
      // If the user is in the iComply transaction group, this activates them server-side.
      await this.configureIcomplyUser();

      // Step 4: Load iComply status (non-critical, continue if fails)
      await this.loadIcomplyStatus();

      // Step 4b: Load reviewer status (non-critical, continue if fails)
      await this.loadReviewerStatus();

      // Step 4c: Load marketing offices (blocking - ensures nav items render correctly on first load)
      // TODO: Once getOfficeAdvisors() has a real backend endpoint, re-evaluate whether this
      // should remain blocking or revert to fire-and-forget with reactive nav items.
      await this.userContextService.loadMarketingOfficesAsync();

      // Step 5: Load onboarding status (required for onboarding guard)
      // This will cache the onboarding status to prevent the guard from making API calls
      // on every route navigation.
      await this.loadOnboardingStatus();

      // Step 6: Start authentication monitoring (cookie expiration polling)
      // This runs globally for both portal and onboarding flows to ensure users
      // are logged out when their session expires, regardless of which route they're on.
      this.authService.pollCookieTimerExpiration();

    } catch (error) {
      console.error('Critical initialization error:', error);
      this.redirectToServiceUnavailable();
      throw error;
    }
  }

  /**
   * Load backend permissions configuration.
   * This is critical - if it fails, we cannot properly guard routes.
   */
  private async loadPermissions(): Promise<void> {
    try {
      await this.permissionService.setPermissions();
    } catch (error) {
      console.error('Failed to load permissions:', error);
      throw new Error('Permissions initialization failed');
    }
  }

  /**
   * Load user context (feature flags, user info, preferences, etc.).
   * This is critical - most of the app depends on this data.
   */
  private async loadUserContext(): Promise<UserContextModel | undefined> {
    try {
      const context = await this.userContextService.getUserContextAsync();
      return context;
    } catch (error) {
      console.error('Failed to load user context:', error);
      throw new Error('User context initialization failed');
    }
  }

  /**
   * Trigger iComply auto-registration. The legacy portal called this during user load so that
   * users tied to the iComply transaction group get activated on sign-in (T20-2097).
   * Non-critical - if iComply is down, we still want the rest of the app to load.
   */
  private async configureIcomplyUser(): Promise<void> {
    try {
      await this.userContextService.configureIcomplyUserAsync();
    } catch (error) {
      console.error('Failed to configure iComply user (non-critical):', error);
    }
  }

  /**
   * Load iComply user status.
   * This is non-critical - if it fails, we continue with undefined.
   * The app can handle missing iComply data gracefully.
   */
  private async loadIcomplyStatus(): Promise<IcomplyUserIsActive | undefined> {
    try {
      const status = await this.userContextService.getUserIcomplyActiveAsync();
      return status;
    } catch (error) {
      console.error('Failed to load iComply status (non-critical):', error);
      // Continue with undefined - app can handle this gracefully
      return undefined;
    }
  }

  private async loadReviewerStatus(): Promise<void> {
    try {
      await this.userContextService.getReviewerStatusAsync();
    } catch (error) {
      console.error('Failed to load reviewer status (non-critical):', error);
    }
  }

  private async loadOnboardingStatus(): Promise<boolean> {
    try {
      const status = await this.onboardingService.loadOnboardingStatusAsync();
      return status;
    } catch (error) {
      console.error('Failed to load onboarding status:', error);
      throw new Error('Onboarding status initialization failed');
    }
  }

  /**
   * Redirect to logout page when user is not authenticated.
   * Directly navigates to logout page without calling authService.logout()
   * to avoid circular redirects (logout would trigger app init again).
   */
  private redirectToLogout(): void {
    window.location.href = '/v2/log-out';
  }

  /**
   * Redirect to service unavailable page when critical initialization fails.
   */
  private redirectToServiceUnavailable(): void {
    window.location.href = '/v2/service-unavailable';
  }
}
